import { useInfiniteQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { blogsQuery } from "@repo/shopify/graphql/blog-gql";
import { useGraphQLClient } from "@repo/shopify/graphql/client";
import { INFINITE_LIST_QUERY_OPTIONS } from "@repo/shopify/graphql/queryClient";
import type { IBlogsQuery } from "@repo/shopify/types/api/blog";

import { NOTIFICATIONS } from "@const/notifications";

import { useLayoutStore } from "@store/layout";

interface IUseFeaturesOptions {
  blogHandle?: string;
  articlesFirst?: number;
  articlesQuery?: string;
}

export default function useFeatures(
  loaderEl?: React.RefObject<HTMLDivElement | null>,
  { blogHandle, articlesFirst = 250, articlesQuery }: IUseFeaturesOptions = {},
) {
  const { search } = useLocation();

  const request = useGraphQLClient();
  const openToast = useLayoutStore((state) => state.openToast);

  const variables = useMemo(
    () => ({
      first: 250,
      reverse: true,
      // blogHandle 이 있으면 그 blog 만 받는다
      query: blogHandle ? `handle:${blogHandle}` : undefined,
      articles_first: articlesFirst,
      articles_sortKey: "PUBLISHED_AT",
      articles_reverse: true,
      // 빈 문자열을 보내면 빈 검색어로 필터링돼 버리므로 없을 땐 아예 안 보낸다
      articles_query: articlesQuery || undefined,
    }),
    [blogHandle, articlesFirst, articlesQuery],
  );

  const blogsInfinite = useInfiniteQuery({
    ...INFINITE_LIST_QUERY_OPTIONS,
    queryKey: ["shopify", "blogs", variables],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => request<IBlogsQuery>(blogsQuery, { ...variables, after: pageParam }),
    getNextPageParam: (lastPage) => (lastPage.blogs?.pageInfo?.hasNextPage ? (lastPage.blogs.pageInfo.endCursor ?? undefined) : undefined),
  });

  useEffect(() => {
    if (blogsInfinite.error) openToast(NOTIFICATIONS.ERROR_SERVER);
  }, [blogsInfinite.error]);

  /** 로드된 페이지들을 하나의 blogs 커넥션으로 합친다 */
  const blogs = useMemo(() => {
    const pages = blogsInfinite.data?.pages;
    if (!pages?.length) return undefined;

    const last = pages[pages.length - 1].blogs;

    return {
      ...last,
      edges: pages.flatMap((page) => page.blogs?.edges ?? []),
      pageInfo: last.pageInfo,
    };
  }, [blogsInfinite.data]);

  /** blogHandle 이 있으면 그 blog 의 아티클, 없으면 전체 blog 를 합쳐 최신순 */
  const articles = useMemo(() => {
    const merged = blogs?.edges?.flatMap(({ node }) => node.articles?.edges || []) || [];

    // 단일 blog 는 서버가 이미 PUBLISHED_AT 순으로 주므로 그대로 쓴다
    if (blogHandle) return merged;

    return [...merged].sort((prev, curr) => new Date(curr.node.publishedAt).getTime() - new Date(prev.node.publishedAt).getTime());
  }, [blogs?.edges, blogHandle]);

  /** blog 중 하나라도 아티클이 더 남아 있으면 true */
  const hasNextPage = useMemo(() => blogs?.edges?.some(({ node }) => node.articles?.pageInfo?.hasNextPage) || false, [blogs?.edges]);

  /** blogs 커넥션 자체의 다음 페이지 (blog 가 250개를 넘을 때만 의미가 있다) */
  const hasMoreBlogs = blogs?.pageInfo?.hasNextPage || false;

  useEffect(() => {
    const el = loaderEl?.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreBlogs && !blogsInfinite.isFetchingNextPage) void blogsInfinite.fetchNextPage();
      },
      { root: null, rootMargin: "1000px 0px 0px 0px", threshold: 0.1 },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [loaderEl, hasMoreBlogs, blogsInfinite.isFetchingNextPage]);

  return {
    articles,
    isFetching: blogsInfinite.isFetchingNextPage,
    loading: blogsInfinite.isFetching,
    hasNextPage,
    search,
    blogs,
  };
}
